import React, { useMemo, useRef } from "react";
import * as S from "../OverviewHero.styles";
import { useResizeDetector } from "react-resize-detector";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";
import { IOverviewHeroSlide } from "../OverviewHero.types";
import { usePlayVideoInView } from "hooks/usePlayInView";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";
import { BREAKPOINTS } from "layout";

import displaySmallPng from "../assets/display-small.png";
import displayPng from "../assets/Display.png";

const INITIAL_WIDTH = 1388;
interface IProDisplayVideoProps {
  activeTab: IOverviewHeroSlide;
  isInView?: boolean;
  onVideoEnded: () => void;
  onStartVideoPlayback: (e: React.SyntheticEvent<HTMLVideoElement>) => void;
}

export const ProDisplayVideo = ({
  activeTab,
  isInView,
  onVideoEnded,
  onStartVideoPlayback,
}: Readonly<IProDisplayVideoProps>) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { width, ref } = useResizeDetector();

  useVideoCleanup(videoRef);
  usePlayVideoInView(videoRef, isInView, true);

  const imageScale = useMemo(() => {
    if (width >= INITIAL_WIDTH) {
      return 1;
    }

    const ratio = width / INITIAL_WIDTH;

    return Number.isNaN(Number(ratio)) ? 1 : Number(ratio.toFixed(3));
  }, [width]);

  return (
    <S.DisplayContainer>
      <S.DisplayBackground ref={ref}>
        <S.AbsoluteContainer>
          <picture>
            <source
              media={BREAKPOINTS.MOBILE_AND_DOWN}
              src={getOptimizedImageUrl(absoluteAssetUrl(displaySmallPng), 619)}
              srcSet={`${getOptimizedImageUrl(
                absoluteAssetUrl(displaySmallPng),
                1238,
              )} 2x`}
            />

            <S.StyledDisplay
              alt="Display Pro"
              src={getOptimizedImageUrl(absoluteAssetUrl(displayPng), 1388)}
              srcSet={`${getOptimizedImageUrl(
                absoluteAssetUrl(displayPng),
                2760,
              )} 2x`}
            />
          </picture>

          <S.ImageLoader $scale={imageScale} />

          <S.StyledVideo
            ref={videoRef}
            $scale={imageScale}
            src={activeTab?.video}
            muted
            poster={getOptimizedImageUrl(
              absoluteAssetUrl(activeTab?.poster),
              1920 * 2,
            )}
            playsInline
            preload="none"
            autoPlay={isInView}
            onCanPlay={onStartVideoPlayback}
            onEnded={onVideoEnded}
          />
        </S.AbsoluteContainer>
      </S.DisplayBackground>
    </S.DisplayContainer>
  );
};
